import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { type ReactNode, Suspense, useCallback } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Button } from '@/components/atoms/button';
import { ErrorState } from '@/components/molecules/ErrorState';
import { Loading } from '@/components/molecules/Loading';
import { reportError } from '@/libs/bugsnag';

interface FallbackRenderProps extends FallbackProps {
  message: string;
  hideRetryButton: boolean;
}

const FallbackRender: React.FC<FallbackRenderProps> = ({
  resetErrorBoundary,
  message,
  hideRetryButton,
}) => {
  return (
    <ErrorState
      title={message}
      actions={
        !hideRetryButton && (
          <Button variant="outline" size="sm" onClick={resetErrorBoundary}>
            Try again
          </Button>
        )
      }
    />
  );
};

interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  errorMessage?: string;
  hideRetryButton?: boolean;
  loadingFallback?: ReactNode;
}

export function ErrorBoundaryWrapper({
  children,
  errorMessage,
  hideRetryButton = false,
  loadingFallback,
}: ErrorBoundaryWrapperProps) {
  const fallbackRender = useCallback(
    (props: FallbackProps) => (
      <FallbackRender
        {...props}
        message={errorMessage ?? 'Page could not load, please contact support.'}
        hideRetryButton={hideRetryButton}
      />
    ),
    [hideRetryButton, errorMessage],
  );

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary fallbackRender={fallbackRender} onReset={reset} onError={reportError}>
          <Suspense fallback={loadingFallback ?? <Loading />}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
